import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import type { DdqSession, DdqSessionQuestion, ReviewStats } from '@/types/api.types';

type UploadMode = 'prepopulate' | 'seed';

interface SessionState {
  // Current session data
  currentSessionId: string | null;
  currentSession: DdqSession | null;
  questions: DdqSessionQuestion[];
  stats: ReviewStats | null;

  // Upload configuration
  uploadMode: UploadMode;
  firmId: number | null;
  productId: number | null;

  // Persistence tracking
  lastSavedAt: string | null;
  isDirty: boolean;

  // Actions
  setCurrentSession: (session: DdqSession | null) => void;
  setCurrentSessionId: (id: string | null) => void;
  setQuestions: (questions: DdqSessionQuestion[]) => void;
  updateQuestion: (questionId: string, updates: Partial<DdqSessionQuestion>) => void;
  setStats: (stats: ReviewStats | null) => void;
  setUploadMode: (mode: UploadMode) => void;
  setFirmId: (firmId: number | null) => void;
  setProductId: (productId: number | null) => void;
  markDirty: () => void;
  markSaved: () => void;
  reset: () => void;
}

const initialState = {
  currentSessionId: null,
  currentSession: null,
  questions: [],
  stats: null,
  uploadMode: 'prepopulate' as UploadMode,
  firmId: null,
  productId: null,
  lastSavedAt: null,
  isDirty: false,
};

export const useSessionStore = create<SessionState>()(
  persist(
    (set) => ({
      ...initialState,

      setCurrentSession: (currentSession) => set({ currentSession }),
      setCurrentSessionId: (currentSessionId) => set({ currentSessionId }),
      setQuestions: (questions) => set({ questions }),

      updateQuestion: (questionId, updates) =>
        set((state) => ({
          questions: state.questions.map((q) =>
            q.question_id === questionId ? { ...q, ...updates } : q
          ),
          isDirty: true,
        })),

      setStats: (stats) => set({ stats }),
      setUploadMode: (uploadMode) => set({ uploadMode }),
      setFirmId: (firmId) => set({ firmId }),
      setProductId: (productId) => set({ productId }),

      markDirty: () => set({ isDirty: true }),

      markSaved: () =>
        set({
          isDirty: false,
          lastSavedAt: new Date().toISOString(),
        }),

      reset: () => set(initialState),
    }),
    {
      name: 'apx-insight-session',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        currentSessionId: state.currentSessionId,
        questions: state.questions,
        firmId: state.firmId,
        productId: state.productId,
        isDirty: state.isDirty,
        lastSavedAt: state.lastSavedAt,
      }),
    }
  )
);
